import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { getStorefrontConfig } from "lib/storefront";
import { isPermissionKeyDisabledForCompany } from "lib/companyRouteRules";
import {
  getStaffFrontendUrl,
  isMobileRoutePath,
  isMobileStaffRoute,
  isStaffRole,
  isV1StaffRoute,
} from "lib/staffFrontend";

function StaffFrontendRedirect() {
  useEffect(() => {
    window.location.replace(getStaffFrontendUrl("/"));
  }, []);

  return null;
}

export default function SecureRoute({ children, roles, pluginRoles, permissionKey }) {
  const { token, profile, pluginRole, authLoading, permissionsLoading, hasPermission, activeCompany, allowedCompanies, setActiveCompany } = useAuth();
  const location = useLocation();
  const storefront = getStorefrontConfig();
  const isSpanish = String(storefront?.locale || "").toLowerCase().startsWith("es");
  const text = isSpanish
    ? {
        selectCompany: "Selecciona empresa",
        sessionNeedsCompany: "Esta sesión necesita una empresa activa para cargar datos.",
        noCompanies: "Tu usuario no pertenece a ninguna empresa. Pide a un administrador que te asigne empresas en Settings.",
      }
    : {
        selectCompany: "Select company",
        sessionNeedsCompany: "This session needs an active company to load data.",
        noCompanies: "Your user does not belong to any company. Ask an admin to assign companies in Settings.",
      };
  const isMobilePath = isMobileRoutePath(location.pathname);
  const signInPath = isMobilePath ? "/m/authentication/sign-in" : "/authentication/sign-in";
  const restrictedPath = isMobilePath ? "/m/restricted" : "/restricted";

  if (!token) return <Navigate to={signInPath} state={{ from: location }} replace />;
  // Avoid flashing redirects while auth is still being hydrated
  if (authLoading || permissionsLoading) return null;
  // Avoid misrouting when token exists but role hasn't been hydrated yet (common on refresh).
  if (token && pluginRole == null) return null;
  if (
    isStaffRole(pluginRole) &&
    (isV1StaffRoute(location.pathname) || isMobileStaffRoute(location.pathname))
  ) {
    return <StaffFrontendRedirect />;
  }
  if (permissionKey && isPermissionKeyDisabledForCompany(activeCompany, permissionKey)) {
    return <Navigate to={restrictedPath} replace />;
  }
  if (Array.isArray(pluginRoles) && pluginRoles.length > 0) {
    const normalizedPluginRole = String(pluginRole || "").trim().toLowerCase();
    const allowedPluginRoles = pluginRoles.map((value) => String(value || "").trim().toLowerCase()).filter(Boolean);
    if (!allowedPluginRoles.includes(normalizedPluginRole)) return <Navigate to={restrictedPath} replace />;
  }
  if (roles && profile && !roles.includes(profile.appRole)) return <Navigate to={restrictedPath} replace />;
  if (!hasPermission(permissionKey)) return <Navigate to={restrictedPath} replace />;

  // Company context gate: prevent the app from firing scoped requests without a company.
  if (!activeCompany) {
    const companiesRel = profile?.companies;
    const companiesData = Array.isArray(companiesRel?.data) ? companiesRel.data : Array.isArray(companiesRel) ? companiesRel : [];
    const profileAllowedKeys = companiesData
      .map((c) => c?.attributes?.key ?? c?.key)
      .filter(Boolean)
      .map((k) => String(k).toLowerCase());

    const roleAllowedKeys = Array.isArray(allowedCompanies)
      ? allowedCompanies.map((key) => String(key || "").trim().toLowerCase()).filter(Boolean)
      : [];
    const companyOptions = Array.from(new Set(roleAllowedKeys.length ? roleAllowedKeys : profileAllowedKeys));

    return (
      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <Card variant="outlined" sx={{ maxWidth: 520, width: "100%", p: 2.5 }}>
          <Typography variant="h6" fontWeight={800} gutterBottom>
            {text.selectCompany}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {text.sessionNeedsCompany}
          </Typography>
          <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
            {companyOptions.map((key) => (
              <Button key={key} variant="contained" onClick={() => setActiveCompany(key)}>
                {String(key).toUpperCase()}
              </Button>
            ))}
          </Box>
          {companyOptions.length === 0 ? (
            <Typography variant="caption" color="error" sx={{ mt: 2, display: "block" }}>
              {text.noCompanies}
            </Typography>
          ) : null}
        </Card>
      </Box>
    );
  }

  return children;
}

SecureRoute.propTypes = {
  children: PropTypes.node,
  roles: PropTypes.arrayOf(PropTypes.string),
  pluginRoles: PropTypes.arrayOf(PropTypes.string),
  permissionKey: PropTypes.string,
};
